export interface RWMockQuestion {
  id: number;
  domain: 'Information & Ideas' | 'Craft & Structure' | 'Expression of Ideas' | 'Standard English Conventions';
  subtopic: string;
  passage?: string;
  question: string;
  options: { label: string; value: string }[];
  correctAnswer: string;
  solution: {
    steps: string[];
    explanation: string;
  };
}

export const mockExamRWQuestions: RWMockQuestion[] = [
  // ── INFORMATION & IDEAS (14 questions) ─────────────────────────────────────
  {
    id: 1,
    domain: 'Information & Ideas',
    subtopic: 'Central Ideas & Details',
    passage: 'The monarch butterfly undertakes one of the most remarkable migrations in the animal kingdom. Each autumn, millions of monarchs travel up to 3,000 miles from Canada and the United States to their overwintering grounds in the mountains of central Mexico. Unlike most migratory animals, no single butterfly completes the full round trip — it takes multiple generations to make the journey north in spring. Scientists believe monarchs navigate using a time-compensated sun compass, adjusting their flight direction based on the position of the sun and an internal circadian clock.',
    question: 'What is the central idea of the passage?',
    options: [
      { label: 'A', value: 'Monarch butterflies are endangered due to habitat loss.' },
      { label: 'B', value: 'The monarch butterfly migration is a multigenerational journey guided by sophisticated navigation.' },
      { label: 'C', value: 'Scientists have fully decoded how monarch butterflies use the sun to navigate.' },
      { label: 'D', value: 'Monarch butterflies travel farther than any other migratory animal.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Identify what the passage is mostly about: the monarch migration.',
        'Note key details: multigenerational, 3,000 miles, sun compass navigation.',
        'Choice B captures both the scale of the migration and the navigation mechanism.',
        'Choice A is not mentioned; C overstates ("fully decoded"); D is not supported.',
      ],
      explanation: 'The central idea encompasses the main topic and the most important supporting details — here, the extraordinary nature of the migration and how it works.',
    },
  },
  {
    id: 2,
    domain: 'Information & Ideas',
    subtopic: 'Command of Evidence',
    passage: 'A 2022 study published in Nature found that urban trees reduce surface temperatures by an average of 2.5°C in summer. Researchers analyzed satellite thermal data from 293 cities worldwide and found the cooling effect was strongest in cities with tree canopy coverage above 30%. The study also noted that low-income neighborhoods in those cities had, on average, 25% less tree coverage than wealthier areas, resulting in measurably higher heat exposure for residents.',
    question: 'Which finding from the study best supports the claim that tree coverage has equity implications?',
    options: [
      { label: 'A', value: 'Urban trees reduce surface temperatures by 2.5°C on average.' },
      { label: 'B', value: 'The cooling effect is strongest above 30% canopy coverage.' },
      { label: 'C', value: 'Low-income neighborhoods have 25% less tree coverage and higher heat exposure.' },
      { label: 'D', value: 'The study analyzed 293 cities worldwide.' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The claim is about equity — fairness across income groups.',
        'Choice C directly links income level to tree coverage and heat exposure.',
        'Choices A and B are about temperature effects, not equity.',
        'Choice D is a methodological detail, not an equity finding.',
      ],
      explanation: 'Command of evidence questions ask you to find the specific detail that supports a stated claim. Match the claim\'s focus (equity) to the evidence.',
    },
  },
  {
    id: 3,
    domain: 'Information & Ideas',
    subtopic: 'Inferences',
    passage: 'When the Rosetta Stone was discovered in 1799, scholars could read the Greek text at the bottom but not the Egyptian hieroglyphics at the top. For over two decades, the stone sat as a tantalizing puzzle. Thomas Young made early progress by identifying that some hieroglyphic symbols represented sounds rather than ideas. Jean-François Champollion, building on Young\'s work and his own deep knowledge of Coptic — a late form of Egyptian — finally cracked the code in 1822, opening up thousands of years of ancient Egyptian writing to modern understanding.',
    question: 'Based on the passage, what can be reasonably inferred about Champollion\'s success?',
    options: [
      { label: 'A', value: 'He succeeded entirely on his own without any prior scholarship.' },
      { label: 'B', value: 'His knowledge of Coptic was irrelevant to deciphering hieroglyphics.' },
      { label: 'C', value: 'His success depended on both earlier work and his unique linguistic knowledge.' },
      { label: 'D', value: 'He discovered the Rosetta Stone himself in 1799.' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The passage says Champollion built on Young\'s work AND used his Coptic knowledge.',
        'Choice A contradicts "building on Young\'s work."',
        'Choice B contradicts the passage\'s explicit mention of Coptic as key.',
        'Choice D contradicts the 1799 discovery date (before Champollion\'s work).',
      ],
      explanation: 'Inference questions require combining multiple details. The passage explicitly credits both prior work and Champollion\'s own expertise.',
    },
  },
  {
    id: 4,
    domain: 'Information & Ideas',
    subtopic: 'Central Ideas & Details',
    passage: 'Fermentation is one of humanity\'s oldest food technologies. Long before the science was understood, cultures around the world independently discovered that allowing certain foods to sit under controlled conditions transformed them into more flavorful, longer-lasting products. Yogurt, cheese, wine, beer, kimchi, and sourdough bread all result from microbial activity — bacteria or yeast consuming sugars and producing acids, alcohols, or gases as byproducts. Modern food science has confirmed what ancient peoples knew empirically: fermentation not only preserves food but often enhances its nutritional profile.',
    question: 'According to the passage, which statement about fermentation is best supported?',
    options: [
      { label: 'A', value: 'Fermentation was invented in a single culture and spread globally.' },
      { label: 'B', value: 'Ancient peoples understood the microbiology behind fermentation.' },
      { label: 'C', value: 'Fermentation was practiced worldwide before its science was understood.' },
      { label: 'D', value: 'Fermentation always produces alcohol as a byproduct.' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The passage states cultures "independently discovered" fermentation — ruling out A.',
        'The passage says the science was NOT understood at first — ruling out B.',
        'Choice C matches "Long before the science was understood, cultures around the world independently discovered..."',
        'Choice D is too absolute — the passage lists acids, alcohols, OR gases.',
      ],
      explanation: 'Always match your answer to what the passage explicitly states. Watch for absolute words like "always" that may not be supported.',
    },
  },
  {
    id: 5,
    domain: 'Information & Ideas',
    subtopic: 'Command of Evidence',
    passage: 'Text 1: Proponents of four-day workweeks argue that reducing work hours increases employee productivity. When workers have more time to rest, they return to work more focused and creative, ultimately producing more output per hour than they would in a traditional five-day schedule.\n\nText 2: A 2023 meta-analysis of 27 four-day workweek trials found mixed results. While 18 of the 27 companies reported maintained or improved productivity, 9 reported declines. The analysis also noted that productivity gains were concentrated in knowledge-work sectors and were less consistent in manufacturing and service industries.',
    question: 'How does Text 2 relate to the argument in Text 1?',
    options: [
      { label: 'A', value: 'Text 2 fully confirms the claim in Text 1 with strong evidence.' },
      { label: 'B', value: 'Text 2 partially supports Text 1 but shows the effect is not universal.' },
      { label: 'C', value: 'Text 2 directly contradicts Text 1 by showing productivity always declines.' },
      { label: 'D', value: 'Text 2 is unrelated to the argument in Text 1.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Text 1 claims four-day weeks increase productivity universally.',
        'Text 2 shows 18/27 companies improved — partial support.',
        'But 9 declined, and gains were sector-specific — not universal.',
        'This is partial support with nuance, matching Choice B.',
      ],
      explanation: 'Dual-text questions ask how two passages relate. Look for agreement, disagreement, or nuance between them.',
    },
  },
  {
    id: 6,
    domain: 'Information & Ideas',
    subtopic: 'Inferences',
    passage: 'The deep ocean, below 200 meters, receives no sunlight and was long assumed to be nearly lifeless. However, submersible expeditions beginning in the 1970s revealed thriving ecosystems around hydrothermal vents — cracks in the ocean floor that release superheated, mineral-rich water. These communities are powered not by photosynthesis but by chemosynthesis, in which bacteria convert chemicals from the vents into energy. Tube worms, giant clams, and unique species of shrimp have all been found living in these extreme environments.',
    question: 'What does the passage suggest about the assumptions scientists held before the 1970s?',
    options: [
      { label: 'A', value: 'Scientists believed the deep ocean was rich with photosynthetic life.' },
      { label: 'B', value: 'Scientists underestimated the diversity of life possible without sunlight.' },
      { label: 'C', value: 'Scientists had already discovered hydrothermal vents before the 1970s.' },
      { label: 'D', value: 'Scientists believed chemosynthesis was the primary energy source in all oceans.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The passage says the deep ocean "was long assumed to be nearly lifeless."',
        'The 1970s expeditions revealed thriving ecosystems — contradicting that assumption.',
        'This implies scientists underestimated what life could exist without sunlight.',
        'Choice B captures this inference correctly.',
      ],
      explanation: 'Inference questions ask what is implied, not just stated. The contrast between "assumed lifeless" and "thriving ecosystems" implies an underestimation.',
    },
  },
  {
    id: 7,
    domain: 'Information & Ideas',
    subtopic: 'Central Ideas & Details',
    passage: 'The placebo effect — improvement in a patient\'s condition due to a treatment with no active therapeutic ingredient — has long puzzled medical researchers. Recent neuroimaging studies suggest that placebos trigger real physiological changes: the brain releases endorphins and dopamine in response to the expectation of treatment. Remarkably, some studies show that "open-label" placebos, where patients are told they are receiving a sugar pill, still produce measurable improvements. This suggests the effect may be driven not just by belief but by the ritual of receiving care itself.',
    question: 'Which detail best supports the idea that the placebo effect involves real biological processes?',
    options: [
      { label: 'A', value: 'Patients improve even when told they are receiving a sugar pill.' },
      { label: 'B', value: 'The brain releases endorphins and dopamine in response to placebo treatment.' },
      { label: 'C', value: 'The placebo effect has puzzled researchers for a long time.' },
      { label: 'D', value: 'The ritual of receiving care may contribute to the effect.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The claim is about "real biological processes."',
        'Choice B directly mentions brain chemistry — endorphins and dopamine — which are biological.',
        'Choice A is about open-label placebos, not biology specifically.',
        'Choice C is about researcher puzzlement, not biology.',
      ],
      explanation: 'Match the specific claim to the most direct evidence. "Real biological processes" maps directly to neurochemical changes.',
    },
  },
  {
    id: 8,
    domain: 'Information & Ideas',
    subtopic: 'Command of Evidence',
    passage: 'Researchers studying sleep deprivation found that adults who slept fewer than 6 hours per night for two weeks performed as poorly on cognitive tests as subjects who had been awake for 24 hours straight. Notably, the sleep-deprived subjects consistently underestimated their own impairment, rating their alertness as near-normal even as their performance declined. The researchers concluded that chronic sleep restriction creates a "sleep debt" that impairs judgment without the subjective feeling of sleepiness.',
    question: 'A student claims: "People who are sleep-deprived are aware of how impaired they are." Which finding from the passage most directly challenges this claim?',
    options: [
      { label: 'A', value: 'Sleep-deprived adults performed as poorly as those awake for 24 hours.' },
      { label: 'B', value: 'Sleep-deprived subjects rated their alertness as near-normal despite declining performance.' },
      { label: 'C', value: 'Chronic sleep restriction creates a "sleep debt."' },
      { label: 'D', value: 'The study lasted two weeks.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The claim is that sleep-deprived people ARE aware of their impairment.',
        'Choice B directly contradicts this: subjects rated themselves as near-normal despite poor performance.',
        'Choice A shows impairment exists but doesn\'t address awareness.',
        'Choices C and D don\'t address self-awareness.',
      ],
      explanation: 'Find the evidence that most directly opposes the stated claim. The key word is "aware" — look for evidence about self-perception.',
    },
  },
  {
    id: 9,
    domain: 'Information & Ideas',
    subtopic: 'Inferences',
    passage: 'In the 19th century, the germ theory of disease — the idea that microorganisms cause illness — was not universally accepted. Ignaz Semmelweis demonstrated in 1847 that handwashing dramatically reduced mortality in maternity wards, yet his findings were largely rejected by the medical establishment. It was not until Louis Pasteur and Robert Koch provided rigorous experimental evidence in the 1860s–1880s that germ theory gained widespread acceptance. Semmelweis died in 1865, never seeing his ideas vindicated.',
    question: 'What can be inferred about the medical establishment\'s response to Semmelweis?',
    options: [
      { label: 'A', value: 'The medical establishment immediately adopted handwashing after Semmelweis\'s findings.' },
      { label: 'B', value: 'The medical establishment required more rigorous experimental proof before accepting new theories.' },
      { label: 'C', value: 'The medical establishment rejected germ theory entirely even after Pasteur and Koch.' },
      { label: 'D', value: 'Semmelweis\'s work was unknown to Pasteur and Koch.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Semmelweis\'s practical evidence was rejected; Pasteur and Koch\'s "rigorous experimental evidence" was accepted.',
        'This contrast implies the establishment needed more rigorous proof.',
        'Choice A contradicts "largely rejected."',
        'Choice C contradicts acceptance after Pasteur and Koch.',
      ],
      explanation: 'The contrast between what was rejected and what was accepted reveals the standard the establishment required.',
    },
  },
  {
    id: 10,
    domain: 'Information & Ideas',
    subtopic: 'Central Ideas & Details',
    passage: 'Coral reefs cover less than 1% of the ocean floor yet support approximately 25% of all marine species. Often called the "rainforests of the sea," reefs provide habitat, feeding grounds, and nurseries for thousands of fish species. They also protect coastlines from storm surges and erosion. Despite their importance, coral reefs face severe threats from rising ocean temperatures, ocean acidification, and pollution. Scientists estimate that 50% of the world\'s coral reefs have been lost since the 1950s, and projections suggest that without significant intervention, up to 90% could be lost by 2050.',
    question: 'Which statement best summarizes the main point of the passage?',
    options: [
      { label: 'A', value: 'Coral reefs are ecologically vital but are being rapidly destroyed.' },
      { label: 'B', value: 'Coral reefs cover a surprisingly large portion of the ocean floor.' },
      { label: 'C', value: 'Ocean acidification is the primary threat to coral reefs.' },
      { label: 'D', value: 'Marine species cannot survive without coral reefs.' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'The passage covers both the importance of reefs AND the threats they face.',
        'Choice A captures both aspects: "ecologically vital" and "rapidly destroyed."',
        'Choice B is inaccurate — reefs cover LESS than 1%.',
        'Choice C singles out one threat; the passage lists multiple.',
      ],
      explanation: 'A summary must capture the full scope of the passage — both the value of reefs and the crisis they face.',
    },
  },
  {
    id: 11,
    domain: 'Information & Ideas',
    subtopic: 'Command of Evidence',
    passage: 'A longitudinal study tracked 1,200 adults over 20 years and found that those who regularly engaged in creative hobbies — painting, writing, music, or crafts — were 73% less likely to develop mild cognitive impairment than those who did not. The effect held even after controlling for education level, physical activity, and social engagement. Researchers hypothesized that creative activities build "cognitive reserve" — a resilience that allows the brain to compensate for age-related decline.',
    question: 'Which detail from the passage most strongly supports the researchers\' hypothesis about cognitive reserve?',
    options: [
      { label: 'A', value: 'The study tracked 1,200 adults over 20 years.' },
      { label: 'B', value: 'Creative hobby participants were 73% less likely to develop cognitive impairment, even after controlling for other factors.' },
      { label: 'C', value: 'The hobbies studied included painting, writing, music, and crafts.' },
      { label: 'D', value: 'Cognitive reserve allows the brain to compensate for decline.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The hypothesis is that creative activities build cognitive reserve.',
        'Choice B shows the effect is specifically tied to creative activities (not education or exercise).',
        'The "even after controlling for other factors" part isolates the creative activity effect.',
        'Choice D restates the hypothesis itself, not evidence for it.',
      ],
      explanation: 'Evidence must support the hypothesis. The controlled finding isolates creative activity as the key variable.',
    },
  },
  {
    id: 12,
    domain: 'Information & Ideas',
    subtopic: 'Inferences',
    passage: 'The Voynich manuscript, a 15th-century illustrated codex written in an unknown script, has defied decipherment for over a century. Cryptographers, linguists, and historians have proposed dozens of theories: a constructed language, an elaborate hoax, an encrypted natural language, or a lost dialect. Statistical analyses of the text show patterns consistent with natural language — letter frequency distributions, word length patterns — yet no one has successfully translated even a single sentence. In 2019, a researcher claimed to have identified the language as proto-Romance, but the claim was widely disputed.',
    question: 'Based on the passage, what can be inferred about the Voynich manuscript?',
    options: [
      { label: 'A', value: 'The manuscript has been definitively proven to be a hoax.' },
      { label: 'B', value: 'The manuscript\'s origin and meaning remain genuinely uncertain despite extensive study.' },
      { label: 'C', value: 'The 2019 proto-Romance theory is now the accepted explanation.' },
      { label: 'D', value: 'Statistical analysis has proven the manuscript contains no meaningful language.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The passage lists many competing theories — none confirmed.',
        'The 2019 claim was "widely disputed" — not accepted.',
        'Statistical patterns suggest language but no translation has succeeded.',
        'Choice B captures the ongoing uncertainty despite extensive study.',
      ],
      explanation: 'When a passage presents multiple unresolved theories, the inference is that the matter remains uncertain.',
    },
  },
  {
    id: 13,
    domain: 'Information & Ideas',
    subtopic: 'Central Ideas & Details',
    passage: 'Biomimicry is the practice of designing products and systems inspired by nature\'s solutions. The bullet train\'s nose was redesigned after the kingfisher bird\'s beak to reduce the sonic boom it created entering tunnels. Velcro was invented after a Swiss engineer noticed how burr seeds clung to his dog\'s fur. Researchers studying shark skin have developed swimsuit fabrics that reduce drag. Proponents argue that billions of years of evolution have already solved many of the engineering challenges humans face, making nature an unparalleled design library.',
    question: 'What is the primary purpose of the examples in the passage?',
    options: [
      { label: 'A', value: 'To argue that human engineers are inferior to natural processes.' },
      { label: 'B', value: 'To illustrate how nature-inspired design has produced practical innovations.' },
      { label: 'C', value: 'To explain the biology of kingfishers, burr seeds, and sharks.' },
      { label: 'D', value: 'To show that biomimicry is only useful in transportation design.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The examples (bullet train, Velcro, swimsuits) all show real products inspired by nature.',
        'They illustrate the concept of biomimicry producing practical results.',
        'Choice A is too negative — the passage doesn\'t say humans are inferior.',
        'Choice C is wrong — the biology is incidental, not the focus.',
      ],
      explanation: 'Examples in a passage serve to illustrate or support the main claim. Here they demonstrate biomimicry\'s practical value.',
    },
  },
  {
    id: 14,
    domain: 'Information & Ideas',
    subtopic: 'Command of Evidence',
    passage: 'Linguists studying language acquisition have found that children exposed to two languages from birth develop slightly smaller vocabularies in each language compared to monolingual peers at age 3. However, when both languages are combined, bilingual children\'s total vocabulary is equivalent to or larger than that of monolinguals. By age 5, the gap in individual-language vocabulary largely disappears. Researchers emphasize that bilingualism confers long-term cognitive advantages, including enhanced executive function and delayed onset of dementia symptoms in old age.',
    question: 'A parent worries that raising a child bilingually will permanently limit their vocabulary. Which evidence from the passage most directly addresses this concern?',
    options: [
      { label: 'A', value: 'Bilingual children have smaller vocabularies in each language at age 3.' },
      { label: 'B', value: 'The vocabulary gap largely disappears by age 5, and total vocabulary is equivalent or larger.' },
      { label: 'C', value: 'Bilingualism enhances executive function.' },
      { label: 'D', value: 'Bilingualism delays dementia symptoms.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The concern is about permanent vocabulary limitation.',
        'Choice B directly addresses this: the gap disappears by age 5 and total vocabulary is equivalent or larger.',
        'Choice A would increase the concern, not address it.',
        'Choices C and D are about other benefits, not vocabulary.',
      ],
      explanation: 'Match the evidence to the specific concern. The parent worries about permanent limitation — find evidence that the limitation is temporary.',
    },
  },

  // ── CRAFT & STRUCTURE (14 questions) ───────────────────────────────────────
  {
    id: 15,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The scientist\'s findings were initially met with considerable skepticism, as they seemed to upend decades of established theory. However, after three independent laboratories replicated her results, the scientific community began to _______ her conclusions.',
    question: 'Which word most logically completes the sentence?',
    options: [
      { label: 'A', value: 'dismiss' },
      { label: 'B', value: 'scrutinize' },
      { label: 'C', value: 'embrace' },
      { label: 'D', value: 'fabricate' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The context shifts from skepticism to acceptance after replication.',
        '"Embrace" means to accept or adopt — fits the shift from doubt to acceptance.',
        '"Dismiss" would continue the skepticism, contradicting the shift.',
        '"Scrutinize" means to examine closely — possible but doesn\'t capture acceptance.',
      ],
      explanation: 'Words in context questions require understanding the logical flow. The "however" signals a contrast — from skepticism to acceptance.',
    },
  },
  {
    id: 16,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The diplomat\'s speech was carefully crafted to be _______ — acknowledging the concerns of both sides without fully committing to either position, leaving room for future negotiation.',
    question: 'Which word best fits the blank?',
    options: [
      { label: 'A', value: 'inflammatory' },
      { label: 'B', value: 'ambiguous' },
      { label: 'C', value: 'decisive' },
      { label: 'D', value: 'transparent' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The description: acknowledges both sides without committing to either.',
        '"Ambiguous" means open to multiple interpretations — fits perfectly.',
        '"Decisive" means clear and firm — the opposite of what\'s described.',
        '"Inflammatory" means provocative — contradicts the diplomatic tone.',
      ],
      explanation: 'The phrase "without fully committing to either position" is the key context clue pointing to ambiguity.',
    },
  },
  {
    id: 17,
    domain: 'Craft & Structure',
    subtopic: 'Text Structure & Purpose',
    passage: 'The history of the internet is often told as a triumphant march of progress — from ARPANET to the World Wide Web to smartphones. But this narrative obscures as much as it reveals. For every technology that succeeded, dozens failed. For every standard that became universal, competing standards were abandoned. The story of the internet is also a story of contingency: the web we have is not the only web that could have been.',
    question: 'What is the primary purpose of this passage?',
    options: [
      { label: 'A', value: 'To celebrate the achievements of internet pioneers.' },
      { label: 'B', value: 'To challenge the idea that the internet\'s development was inevitable.' },
      { label: 'C', value: 'To explain the technical history of ARPANET.' },
      { label: 'D', value: 'To argue that the internet has been harmful to society.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The passage critiques the "triumphant march" narrative.',
        'It emphasizes contingency — things could have gone differently.',
        'Choice B captures this: challenging the idea of inevitability.',
        'Choice A is the opposite of the passage\'s critical stance.',
      ],
      explanation: 'The author\'s purpose is to complicate or challenge a common narrative, not to celebrate or explain technically.',
    },
  },
  {
    id: 18,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The new policy was _______ in its scope, affecting not just the department that proposed it but every division of the organization, from finance to human resources to operations.',
    question: 'Which word best completes the sentence?',
    options: [
      { label: 'A', value: 'narrow' },
      { label: 'B', value: 'tentative' },
      { label: 'C', value: 'sweeping' },
      { label: 'D', value: 'redundant' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The policy affects every division — a very wide scope.',
        '"Sweeping" means wide-ranging or comprehensive — fits perfectly.',
        '"Narrow" is the opposite of what\'s described.',
        '"Tentative" means uncertain — doesn\'t relate to scope.',
      ],
      explanation: '"Sweeping" is the best fit because the sentence emphasizes the policy\'s broad reach across all divisions.',
    },
  },
  {
    id: 19,
    domain: 'Craft & Structure',
    subtopic: 'Author\'s Tone & Purpose',
    passage: 'We have been told, repeatedly and with great confidence, that artificial intelligence will transform every industry within the decade. Perhaps. But we have heard similar promises before — about nuclear power, about nanotechnology, about virtual reality. Each wave of technological enthusiasm has crested and receded, leaving behind something useful but far less revolutionary than advertised. Skepticism, in this context, is not pessimism. It is memory.',
    question: 'The author\'s tone in this passage can best be described as:',
    options: [
      { label: 'A', value: 'enthusiastic and optimistic' },
      { label: 'B', value: 'measured and skeptical' },
      { label: 'C', value: 'hostile and dismissive' },
      { label: 'D', value: 'confused and uncertain' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The author acknowledges AI may transform industries ("Perhaps") — not hostile.',
        'But draws on historical patterns to urge caution — skeptical.',
        '"Skepticism is not pessimism. It is memory." — measured, reasoned stance.',
        'Not enthusiastic (A), not hostile (C), not confused (D).',
      ],
      explanation: 'Tone is revealed through word choice and argument structure. "Perhaps" and historical comparisons signal measured skepticism.',
    },
  },
  {
    id: 20,
    domain: 'Craft & Structure',
    subtopic: 'Cross-Text Connections',
    passage: 'Text 1: Social media platforms have democratized information sharing, allowing anyone to publish and distribute content globally. This has amplified marginalized voices and enabled grassroots movements that traditional media would have ignored.\n\nText 2: The same mechanisms that allow anyone to publish content also allow misinformation to spread at unprecedented speed. Studies show that false news spreads six times faster on social media than accurate news, and corrections rarely reach the same audience as the original falsehood.',
    question: 'Both texts discuss social media, but they differ primarily in their:',
    options: [
      { label: 'A', value: 'definitions of social media platforms' },
      { label: 'B', value: 'focus on different aspects — empowerment versus misinformation risks' },
      { label: 'C', value: 'conclusions about whether social media should be regulated' },
      { label: 'D', value: 'audiences — Text 1 addresses users, Text 2 addresses policymakers' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Text 1 focuses on democratization and amplifying voices — empowerment.',
        'Text 2 focuses on misinformation spread — risks.',
        'Both discuss the same mechanism but emphasize different consequences.',
        'Neither text discusses regulation (C) or specific audiences (D).',
      ],
      explanation: 'Cross-text questions ask how two passages relate. Identify each text\'s focus and find the answer that captures the contrast.',
    },
  },
  {
    id: 21,
    domain: 'Craft & Structure',
    subtopic: 'Text Structure & Purpose',
    passage: 'To understand why the Roman Empire fell, one must first resist the temptation of a single cause. Historians have proposed over 200 distinct explanations: military overextension, economic strain, political instability, climate change, pandemic disease, and cultural transformation, among others. Edward Gibbon blamed Christianity; modern historians emphasize structural factors. The fall of Rome was not an event but a process — one that unfolded over centuries and looked different depending on where in the empire you stood.',
    question: 'The author begins by warning against "a single cause." What is the purpose of this opening?',
    options: [
      { label: 'A', value: 'To suggest that the Roman Empire\'s fall is impossible to explain.' },
      { label: 'B', value: 'To prepare the reader for a multifaceted explanation of Rome\'s fall.' },
      { label: 'C', value: 'To argue that Gibbon\'s explanation is the most accurate.' },
      { label: 'D', value: 'To criticize historians who study ancient history.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The opening warns against oversimplification.',
        'The rest of the passage lists many causes — supporting a complex explanation.',
        'This prepares the reader for complexity, matching Choice B.',
        'Choice A is too extreme — the passage doesn\'t say it\'s impossible to explain.',
      ],
      explanation: 'Opening statements often frame the argument. Here, the warning against single causes sets up the multifaceted analysis that follows.',
    },
  },
  {
    id: 22,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The composer\'s later works were marked by a _______ quality — stripped of the lush orchestration of his early career, they relied instead on spare melodic lines and long silences that demanded active listening from the audience.',
    question: 'Which word best fits the blank?',
    options: [
      { label: 'A', value: 'austere' },
      { label: 'B', value: 'ornate' },
      { label: 'C', value: 'chaotic' },
      { label: 'D', value: 'derivative' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        '"Stripped of lush orchestration," "spare melodic lines," "long silences" — all suggest simplicity.',
        '"Austere" means severe simplicity, unadorned — fits perfectly.',
        '"Ornate" means elaborately decorated — the opposite.',
        '"Chaotic" implies disorder — not supported by the description.',
      ],
      explanation: 'Context clues like "stripped," "spare," and "silences" all point to austerity — deliberate simplicity.',
    },
  },
  {
    id: 23,
    domain: 'Craft & Structure',
    subtopic: 'Author\'s Tone & Purpose',
    passage: 'The city\'s new transit plan promises to reduce commute times by 40% and cut carbon emissions by a third. These are admirable goals. But the plan relies on a funding model that has failed in three other cities, a timeline that independent engineers call "optimistic at best," and ridership projections that assume a 25% increase in transit use with no explanation of how that increase will be achieved. Admirable goals, poorly executed, are still failures.',
    question: 'The author\'s attitude toward the transit plan is best described as:',
    options: [
      { label: 'A', value: 'fully supportive of the plan\'s goals and methods' },
      { label: 'B', value: 'supportive of the goals but critical of the plan\'s feasibility' },
      { label: 'C', value: 'indifferent to both the goals and the methods' },
      { label: 'D', value: 'opposed to reducing carbon emissions' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The author calls the goals "admirable" — supportive of goals.',
        'But criticizes the funding model, timeline, and projections — critical of methods.',
        'The final line: "Admirable goals, poorly executed, are still failures" confirms this split.',
        'Choice B captures both the support and the criticism.',
      ],
      explanation: 'Authors can support a goal while criticizing its implementation. Look for the nuanced position.',
    },
  },
  {
    id: 24,
    domain: 'Craft & Structure',
    subtopic: 'Text Structure & Purpose',
    passage: 'Marie Curie\'s discovery of polonium and radium in 1898 was not simply a scientific achievement — it was a defiance of institutional barriers. Working in a leaky shed with inadequate equipment, she became the first person to win two Nobel Prizes in different sciences. Yet she was denied membership in the French Academy of Sciences in 1911, the same year she won her second Nobel Prize, because she was a woman. Her story is simultaneously one of triumph and of the obstacles that genius must sometimes overcome.',
    question: 'The passage presents Curie\'s story primarily as:',
    options: [
      { label: 'A', value: 'a straightforward account of her scientific discoveries' },
      { label: 'B', value: 'a critique of the Nobel Prize selection process' },
      { label: 'C', value: 'a portrait of achievement alongside institutional discrimination' },
      { label: 'D', value: 'an argument that women are better scientists than men' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'The passage covers both her achievements (two Nobels) and discrimination (denied Academy membership).',
        'The final sentence explicitly states "triumph" and "obstacles."',
        'Choice C captures both dimensions.',
        'Choice A ignores the discrimination angle; Choice B is not the focus.',
      ],
      explanation: 'The passage\'s structure — alternating between achievements and barriers — signals a dual portrait.',
    },
  },
  {
    id: 25,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The professor\'s lecture style was _______ — she moved seamlessly from ancient philosophy to modern neuroscience to personal anecdote, weaving disparate threads into a coherent argument that left students both informed and inspired.',
    question: 'Which word best fits the blank?',
    options: [
      { label: 'A', value: 'pedantic' },
      { label: 'B', value: 'eclectic' },
      { label: 'C', value: 'monotonous' },
      { label: 'D', value: 'superficial' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The description: philosophy, neuroscience, personal anecdote — diverse sources.',
        '"Eclectic" means drawing from a wide variety of sources — fits perfectly.',
        '"Pedantic" means overly focused on minor details — not supported.',
        '"Monotonous" means dull and repetitive — contradicted by "seamlessly" and "inspired."',
      ],
      explanation: 'The variety of sources (philosophy, neuroscience, anecdote) is the key context clue pointing to "eclectic."',
    },
  },
  {
    id: 26,
    domain: 'Craft & Structure',
    subtopic: 'Cross-Text Connections',
    passage: 'Text 1: The introduction of wolves to Yellowstone in 1995 triggered a "trophic cascade" — a chain of ecological effects that transformed the park. Wolves reduced elk populations, which allowed riverbank vegetation to recover, which stabilized riverbanks, which changed the course of rivers. The wolves, in effect, changed the rivers.\n\nText 2: While the Yellowstone wolf reintroduction is often cited as a model for rewilding, ecologists caution against overgeneralizing. The specific conditions at Yellowstone — its size, the absence of competing predators, and the particular elk population — may not be replicable elsewhere. Rewilding projects in Europe have produced more mixed results.',
    question: 'How would the author of Text 2 most likely respond to the claim in Text 1?',
    options: [
      { label: 'A', value: 'By arguing that wolves should be removed from Yellowstone.' },
      { label: 'B', value: 'By agreeing that the Yellowstone results are universally applicable.' },
      { label: 'C', value: 'By acknowledging the Yellowstone results while cautioning against assuming they apply everywhere.' },
      { label: 'D', value: 'By claiming that trophic cascades do not exist.' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Text 2 doesn\'t deny the Yellowstone results — it says "often cited as a model."',
        'But it cautions against "overgeneralizing" — the results may not apply elsewhere.',
        'Choice C captures this: acknowledge + caution.',
        'Choice B is the opposite of Text 2\'s position.',
      ],
      explanation: 'Text 2 accepts the Yellowstone findings but limits their generalizability. The response would be nuanced, not dismissive.',
    },
  },
  {
    id: 27,
    domain: 'Craft & Structure',
    subtopic: 'Author\'s Tone & Purpose',
    passage: 'There is something quietly radical about a public library. In an age of paywalls, subscription fees, and algorithmic curation, the library offers something almost subversive: free, unrestricted access to knowledge, curated by professionals whose only agenda is to serve the public. No data is collected. No behavior is monetized. You may sit for hours, read what you wish, and leave without having generated a single data point for a corporation.',
    question: 'The author\'s use of words like "radical," "subversive," and "quietly" suggests:',
    options: [
      { label: 'A', value: 'The author believes libraries are dangerous institutions.' },
      { label: 'B', value: 'The author views libraries as unexpectedly countercultural in the modern digital economy.' },
      { label: 'C', value: 'The author is criticizing libraries for being too quiet.' },
      { label: 'D', value: 'The author thinks libraries should charge subscription fees.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Quietly radical" and "subversive" suggest something that challenges the norm without being loud about it.',
        'The contrast with paywalls and data collection frames libraries as countercultural.',
        'Choice B captures this: unexpectedly countercultural in the digital economy.',
        'Choice A misreads "radical" as threatening rather than unconventional.',
      ],
      explanation: 'Tone words like "quietly radical" signal admiration for something that subtly defies the dominant culture.',
    },
  },
  {
    id: 28,
    domain: 'Craft & Structure',
    subtopic: 'Words in Context',
    passage: 'The athlete\'s recovery was nothing short of _______ — having been told she would never compete again after her injury, she returned to win the national championship just fourteen months later.',
    question: 'Which word best fits the blank?',
    options: [
      { label: 'A', value: 'predictable' },
      { label: 'B', value: 'miraculous' },
      { label: 'C', value: 'gradual' },
      { label: 'D', value: 'controversial' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Nothing short of ___" is an intensifying phrase meaning "truly ___."',
        'The context: told she\'d never compete again, yet won a championship in 14 months.',
        '"Miraculous" means extraordinary, almost impossible — fits the dramatic contrast.',
        '"Predictable" contradicts the surprise; "gradual" doesn\'t fit the intensifying phrase.',
      ],
      explanation: 'The phrase "nothing short of" signals something extraordinary. The dramatic comeback confirms "miraculous."',
    },
  },

  // ── EXPRESSION OF IDEAS (13 questions) ─────────────────────────────────────
  {
    id: 29,
    domain: 'Expression of Ideas',
    subtopic: 'Transitions',
    question: 'The early trials of the vaccine showed promising results. _______, the researchers cautioned that larger studies would be needed before any conclusions could be drawn.',
    options: [
      { label: 'A', value: 'Therefore' },
      { label: 'B', value: 'Nevertheless' },
      { label: 'C', value: 'Similarly' },
      { label: 'D', value: 'As a result' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Sentence 1: promising results (positive).',
        'Sentence 2: caution needed (limiting the positive).',
        'This is a contrast relationship — need a contrast transition.',
        '"Nevertheless" means "despite that" — signals contrast. Correct.',
        '"Therefore" and "As a result" signal cause-effect, not contrast.',
      ],
      explanation: 'Identify the logical relationship between sentences. Positive result → caution = contrast → use "Nevertheless" or "However."',
    },
  },
  {
    id: 30,
    domain: 'Expression of Ideas',
    subtopic: 'Sentence Combining',
    question: 'Which option most effectively combines these two sentences? "The documentary received widespread critical acclaim. It failed to attract a large audience."',
    options: [
      { label: 'A', value: 'The documentary received widespread critical acclaim, and it failed to attract a large audience.' },
      { label: 'B', value: 'The documentary received widespread critical acclaim but failed to attract a large audience.' },
      { label: 'C', value: 'The documentary received widespread critical acclaim; it failed to attract a large audience.' },
      { label: 'D', value: 'The documentary received widespread critical acclaim, so it failed to attract a large audience.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The two ideas contrast: acclaim vs. small audience.',
        'Choice B uses "but" — the correct contrast conjunction — and eliminates the redundant subject.',
        'Choice A uses "and" — doesn\'t capture the contrast.',
        'Choice D uses "so" — implies causation, which is wrong.',
      ],
      explanation: 'When combining contrasting ideas, use "but" or "yet." Eliminate redundant subjects for conciseness.',
    },
  },
  {
    id: 31,
    domain: 'Expression of Ideas',
    subtopic: 'Rhetorical Synthesis',
    question: 'A student is writing about the benefits of urban green spaces. The notes say: (1) Green spaces reduce stress hormones by 15%. (2) Parks increase neighborhood property values. (3) Urban trees reduce air pollution. Which sentence best synthesizes these notes for a paragraph about public health benefits?',
    options: [
      { label: 'A', value: 'Urban green spaces increase property values and reduce air pollution.' },
      { label: 'B', value: 'Urban green spaces offer multiple public health benefits, including stress reduction and improved air quality.' },
      { label: 'C', value: 'Green spaces are expensive to maintain but reduce stress hormones.' },
      { label: 'D', value: 'Property values increase when parks are built in urban neighborhoods.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The paragraph focus is public health benefits.',
        'Notes 1 (stress) and 3 (air pollution) are public health benefits.',
        'Note 2 (property values) is economic, not public health.',
        'Choice B synthesizes the public health notes (stress + air quality) correctly.',
      ],
      explanation: 'Synthesis questions require selecting and combining only the notes relevant to the stated focus.',
    },
  },
  {
    id: 32,
    domain: 'Expression of Ideas',
    subtopic: 'Transitions',
    question: 'The company had invested heavily in its retail locations for decades. _______, the rise of e-commerce forced it to rethink its entire business model.',
    options: [
      { label: 'A', value: 'In addition' },
      { label: 'B', value: 'For instance' },
      { label: 'C', value: 'However' },
      { label: 'D', value: 'Consequently' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Sentence 1: heavy investment in retail (established approach).',
        'Sentence 2: forced to rethink (change/disruption).',
        'This is a contrast — the rise of e-commerce disrupted the established approach.',
        '"However" signals contrast. Correct.',
        '"Consequently" would mean the investment caused the rethinking — illogical.',
      ],
      explanation: 'The shift from established practice to forced change signals a contrast transition.',
    },
  },
  {
    id: 33,
    domain: 'Expression of Ideas',
    subtopic: 'Sentence Combining',
    question: 'Which option most effectively combines these sentences? "The report was thorough. The report was well-researched. The report was ignored by policymakers."',
    options: [
      { label: 'A', value: 'The report was thorough and well-researched, but it was ignored by policymakers.' },
      { label: 'B', value: 'The report was thorough, and it was well-researched, and it was ignored by policymakers.' },
      { label: 'C', value: 'The report was thorough; the report was well-researched; the report was ignored.' },
      { label: 'D', value: 'Being thorough and well-researched, policymakers ignored the report.' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Choice A combines the two positive qualities with "and," then contrasts with "but" for the negative outcome.',
        'Choice B is grammatically correct but awkward with three "and" clauses.',
        'Choice C is repetitive with three semicolons and repeated "the report."',
        'Choice D creates a dangling modifier — policymakers weren\'t thorough.',
      ],
      explanation: 'Effective combination is concise, avoids repetition, and uses the right conjunction to show contrast.',
    },
  },
  {
    id: 34,
    domain: 'Expression of Ideas',
    subtopic: 'Rhetorical Synthesis',
    question: 'A student is writing about challenges facing small businesses. Notes: (1) 20% of small businesses fail in year one. (2) Access to capital is the top challenge cited by small business owners. (3) Small businesses employ 47% of the private workforce. Which sentence best introduces a paragraph about why small business support matters?',
    options: [
      { label: 'A', value: 'Small businesses fail at high rates, with 20% closing in their first year.' },
      { label: 'B', value: 'Small businesses employ nearly half the private workforce, yet they face significant challenges including limited access to capital and high failure rates.' },
      { label: 'C', value: 'Access to capital is the most important factor in small business success.' },
      { label: 'D', value: 'The private workforce depends on large corporations for employment.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The paragraph is about why support matters — need both importance and challenges.',
        'Choice B uses Note 3 (importance: 47% of workforce) and Notes 1 & 2 (challenges).',
        'Choice A only covers failure rates — incomplete.',
        'Choice D contradicts Note 3.',
      ],
      explanation: 'An introductory sentence for "why support matters" needs both the stakes (importance) and the problem (challenges).',
    },
  },
  {
    id: 35,
    domain: 'Expression of Ideas',
    subtopic: 'Transitions',
    question: 'Researchers initially believed the ancient site was a burial ground. _______, recent excavations have revealed evidence of a thriving marketplace.',
    options: [
      { label: 'A', value: 'Furthermore' },
      { label: 'B', value: 'In contrast' },
      { label: 'C', value: 'As a result' },
      { label: 'D', value: 'Similarly' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Sentence 1: believed to be a burial ground.',
        'Sentence 2: revealed to be a marketplace.',
        'These are contrasting interpretations.',
        '"In contrast" signals a direct contrast between two different things.',
        '"Furthermore" adds information; "Similarly" compares — neither fits.',
      ],
      explanation: 'When new evidence contradicts an earlier belief, use a contrast transition like "In contrast" or "However."',
    },
  },
  {
    id: 36,
    domain: 'Expression of Ideas',
    subtopic: 'Sentence Combining',
    question: 'Which option most effectively combines these sentences? "The new bridge design is innovative. It reduces construction time by 30%. It also lowers costs significantly."',
    options: [
      { label: 'A', value: 'The new bridge design is innovative, reduces construction time by 30%, and lowers costs significantly.' },
      { label: 'B', value: 'The new bridge design is innovative and it reduces construction time by 30% and it also lowers costs significantly.' },
      { label: 'C', value: 'The new bridge design is innovative; it reduces construction time by 30%; it also lowers costs significantly.' },
      { label: 'D', value: 'Being innovative, the new bridge design reduces construction time by 30%, and costs are also lowered.' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        'Choice A uses parallel structure: "is innovative, reduces..., and lowers..." — clean and concise.',
        'Choice B is grammatically weak with repeated "and it."',
        'Choice C uses semicolons correctly but is less fluid than A.',
        'Choice D has a dangling modifier and passive voice ("costs are lowered").',
      ],
      explanation: 'Parallel structure (verb, verb, verb) is the most concise and elegant way to combine three related ideas.',
    },
  },
  {
    id: 37,
    domain: 'Expression of Ideas',
    subtopic: 'Rhetorical Synthesis',
    question: 'A student is writing about the impact of social media on teenagers. Notes: (1) 45% of teens say they are online "almost constantly." (2) Studies link heavy social media use to increased anxiety in teens. (3) Social media allows teens to maintain friendships across distances. Which sentence best introduces a paragraph about the mental health concerns?',
    options: [
      { label: 'A', value: 'Social media helps teenagers maintain friendships, which is an important benefit.' },
      { label: 'B', value: 'While social media offers social benefits, its near-constant use by 45% of teens is linked to increased anxiety.' },
      { label: 'C', value: 'Teenagers use social media almost constantly, which helps them stay connected.' },
      { label: 'D', value: 'Social media companies should be regulated to protect teenagers.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The paragraph is about mental health concerns.',
        'Choice B acknowledges the benefit (Note 3) but pivots to the concern (Notes 1 & 2).',
        'Choice A focuses only on benefits — wrong focus for a mental health paragraph.',
        'Choice D introduces regulation — not in the notes.',
      ],
      explanation: 'A good introductory sentence for a concern paragraph acknowledges the counterpoint but clearly pivots to the main concern.',
    },
  },
  {
    id: 38,
    domain: 'Expression of Ideas',
    subtopic: 'Transitions',
    question: 'The new medication showed no significant side effects in clinical trials. _______, doctors are recommending it as a first-line treatment for the condition.',
    options: [
      { label: 'A', value: 'Despite this' },
      { label: 'B', value: 'Nevertheless' },
      { label: 'C', value: 'As a result' },
      { label: 'D', value: 'In contrast' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Sentence 1: no side effects (positive finding).',
        'Sentence 2: doctors recommend it (logical outcome of the positive finding).',
        'This is a cause-effect relationship.',
        '"As a result" signals cause-effect. Correct.',
        '"Nevertheless" and "Despite this" signal contrast — wrong here.',
      ],
      explanation: 'When the second sentence is a logical outcome of the first, use a cause-effect transition like "As a result" or "Therefore."',
    },
  },
  {
    id: 39,
    domain: 'Expression of Ideas',
    subtopic: 'Sentence Combining',
    question: 'Which option most effectively combines these sentences? "The author wrote the novel in six weeks. She revised it for three years."',
    options: [
      { label: 'A', value: 'The author wrote the novel in six weeks, and she revised it for three years.' },
      { label: 'B', value: 'The author wrote the novel in six weeks but spent three years revising it.' },
      { label: 'C', value: 'The author wrote the novel in six weeks; she revised it for three years.' },
      { label: 'D', value: 'Writing the novel in six weeks, the author revised it for three years.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The contrast is between the short writing time (6 weeks) and long revision time (3 years).',
        'Choice B uses "but" to highlight the contrast and is concise.',
        'Choice A uses "and" — doesn\'t capture the contrast.',
        'Choice C is grammatically correct but less expressive of the contrast.',
      ],
      explanation: '"But" is the best choice when the second clause contrasts with or surprises relative to the first.',
    },
  },
  {
    id: 40,
    domain: 'Expression of Ideas',
    subtopic: 'Rhetorical Synthesis',
    question: 'A student is writing about renewable energy adoption. Notes: (1) Solar panel costs have dropped 89% since 2010. (2) Wind energy now provides 10% of US electricity. (3) Fossil fuel subsidies total $20 billion annually in the US. Which sentence best supports a paragraph arguing that renewable energy is economically competitive?',
    options: [
      { label: 'A', value: 'Fossil fuel subsidies total $20 billion annually, which is unfair to renewable energy.' },
      { label: 'B', value: 'The dramatic drop in solar costs and wind energy\'s growing share of electricity generation demonstrate that renewables are increasingly economically competitive.' },
      { label: 'C', value: 'Wind energy provides 10% of US electricity, which is a small fraction of total energy.' },
      { label: 'D', value: 'Renewable energy will eventually replace all fossil fuels.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The paragraph argues economic competitiveness.',
        'Notes 1 (cost drop) and 2 (growing share) directly support economic competitiveness.',
        'Note 3 (subsidies) is about policy fairness, not competitiveness.',
        'Choice B synthesizes Notes 1 and 2 to support the economic argument.',
      ],
      explanation: 'Select notes that directly support the stated argument. Cost reduction and market share are economic competitiveness indicators.',
    },
  },
  {
    id: 41,
    domain: 'Expression of Ideas',
    subtopic: 'Transitions',
    question: 'The study found that exercise improves memory in older adults. _______, participants who exercised three times per week showed a 20% improvement in recall tests.',
    options: [
      { label: 'A', value: 'However' },
      { label: 'B', value: 'Specifically' },
      { label: 'C', value: 'On the other hand' },
      { label: 'D', value: 'Despite this' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'Sentence 1: general claim — exercise improves memory.',
        'Sentence 2: specific data supporting that claim.',
        'The relationship is general → specific, not contrast.',
        '"Specifically" signals elaboration or specification. Correct.',
        '"However" and "On the other hand" signal contrast — wrong here.',
      ],
      explanation: 'When the second sentence provides a specific example or data point for a general claim, use "Specifically" or "For example."',
    },
  },

  // ── STANDARD ENGLISH CONVENTIONS (13 questions) ────────────────────────────
  {
    id: 42,
    domain: 'Standard English Conventions',
    subtopic: 'Subject-Verb Agreement',
    question: 'The committee, along with several outside advisors, _______ expected to release its final report by Friday.',
    options: [
      { label: 'A', value: 'are' },
      { label: 'B', value: 'is' },
      { label: 'C', value: 'were' },
      { label: 'D', value: 'have been' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The subject is "the committee" — singular.',
        '"Along with several outside advisors" is a prepositional phrase, not part of the subject.',
        'Singular subject → singular verb: "is."',
        '"Are" and "were" are plural forms.',
      ],
      explanation: 'Phrases beginning with "along with," "as well as," or "in addition to" do not change the number of the subject.',
    },
  },
  {
    id: 43,
    domain: 'Standard English Conventions',
    subtopic: 'Punctuation',
    question: 'Which sentence uses a comma correctly?',
    options: [
      { label: 'A', value: 'The scientist, who discovered the compound, published her findings last month.' },
      { label: 'B', value: 'The scientist who discovered the compound, published her findings last month.' },
      { label: 'C', value: 'The scientist who discovered the compound published, her findings last month.' },
      { label: 'D', value: 'The scientist, who discovered the compound published her findings last month.' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        '"Who discovered the compound" is a nonrestrictive clause — it adds information but isn\'t essential to identify the scientist.',
        'Nonrestrictive clauses are set off by commas on both sides.',
        'Choice A correctly places commas before and after the clause.',
        'Choice B has only one comma — incorrect for a nonrestrictive clause.',
      ],
      explanation: 'Nonrestrictive (non-essential) clauses are enclosed in commas. Restrictive (essential) clauses have no commas.',
    },
  },
  {
    id: 44,
    domain: 'Standard English Conventions',
    subtopic: 'Verb Tense',
    question: 'By the time the rescue team arrived, the hikers _______ in the cave for nearly twelve hours.',
    options: [
      { label: 'A', value: 'waited' },
      { label: 'B', value: 'have waited' },
      { label: 'C', value: 'had been waiting' },
      { label: 'D', value: 'are waiting' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '"By the time the rescue team arrived" sets a past reference point.',
        'The hikers\' waiting happened before and up to that past point — past perfect progressive.',
        '"Had been waiting" = past perfect progressive — correct.',
        '"Have waited" is present perfect — wrong tense for a past reference.',
      ],
      explanation: 'Use past perfect (had + past participle) or past perfect progressive (had been + -ing) for actions completed before another past event.',
    },
  },
  {
    id: 45,
    domain: 'Standard English Conventions',
    subtopic: 'Pronoun Usage',
    question: 'Each of the students must submit _______ assignment by midnight.',
    options: [
      { label: 'A', value: 'their' },
      { label: 'B', value: 'his or her' },
      { label: 'C', value: 'its' },
      { label: 'D', value: 'our' },
    ],
    correctAnswer: 'A',
    solution: {
      steps: [
        '"Each" is grammatically singular but refers to individual people.',
        'Modern standard usage accepts "their" as a singular gender-neutral pronoun.',
        '"His or her" is also acceptable but more formal and wordy.',
        'On the SAT, "their" is accepted for singular indefinite pronouns like "each," "everyone," "someone."',
        '"Its" is for non-human antecedents.',
      ],
      explanation: 'The SAT accepts "their" as a singular gender-neutral pronoun. "Its" is incorrect for people.',
    },
  },
  {
    id: 46,
    domain: 'Standard English Conventions',
    subtopic: 'Punctuation',
    question: 'Which sentence correctly uses a semicolon?',
    options: [
      { label: 'A', value: 'The experiment failed; because the temperature was too high.' },
      { label: 'B', value: 'The experiment failed; the temperature was too high.' },
      { label: 'C', value: 'The experiment; failed because the temperature was too high.' },
      { label: 'D', value: 'The experiment failed because; the temperature was too high.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'A semicolon joins two independent clauses.',
        '"The experiment failed" — independent clause.',
        '"The temperature was too high" — independent clause.',
        'Choice B correctly joins two independent clauses with a semicolon.',
        'Choice A: "because the temperature was too high" is a dependent clause — cannot follow a semicolon.',
      ],
      explanation: 'Semicolons connect two independent clauses. Never use a semicolon before a subordinating conjunction like "because."',
    },
  },
  {
    id: 47,
    domain: 'Standard English Conventions',
    subtopic: 'Modifiers',
    question: 'Which sentence correctly places the modifier?',
    options: [
      { label: 'A', value: 'Running through the park, the rain began to fall heavily.' },
      { label: 'B', value: 'Running through the park, she was caught in the heavy rain.' },
      { label: 'C', value: 'She was caught in the heavy rain, running through the park.' },
      { label: 'D', value: 'The heavy rain began to fall, running through the park.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Running through the park" is a participial phrase — it must modify the subject of the main clause.',
        'In Choice B, the subject is "she" — she was running through the park. Correct.',
        'In Choice A, "the rain" is the subject — rain cannot run through a park. Dangling modifier.',
        'In Choice D, "the heavy rain" is the subject — rain cannot run. Dangling modifier.',
      ],
      explanation: 'A participial phrase at the start of a sentence must modify the subject of the main clause. If it doesn\'t, it\'s a dangling modifier.',
    },
  },
  {
    id: 48,
    domain: 'Standard English Conventions',
    subtopic: 'Subject-Verb Agreement',
    question: 'Neither the manager nor the employees _______ aware of the policy change.',
    options: [
      { label: 'A', value: 'was' },
      { label: 'B', value: 'were' },
      { label: 'C', value: 'is' },
      { label: 'D', value: 'has been' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Neither...nor" constructions: the verb agrees with the subject closest to it.',
        'The subject closest to the verb is "the employees" — plural.',
        'Plural subject → plural verb: "were."',
        '"Was" and "is" are singular forms.',
      ],
      explanation: 'With "either...or" and "neither...nor," the verb agrees with the nearer subject (the one closest to the verb).',
    },
  },
  {
    id: 49,
    domain: 'Standard English Conventions',
    subtopic: 'Parallel Structure',
    question: 'Which sentence uses parallel structure correctly?',
    options: [
      { label: 'A', value: 'The coach emphasized practicing daily, to study game film, and mental preparation.' },
      { label: 'B', value: 'The coach emphasized practicing daily, studying game film, and mental preparation.' },
      { label: 'C', value: 'The coach emphasized practicing daily, studying game film, and preparing mentally.' },
      { label: 'D', value: 'The coach emphasized to practice daily, studying game film, and mental preparation.' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        'Parallel structure requires the same grammatical form for all items in a list.',
        'Choice C: practicing (gerund), studying (gerund), preparing (gerund) — all gerunds. Parallel.',
        'Choice B: practicing (gerund), studying (gerund), mental preparation (noun phrase) — not parallel.',
        'Choice A: practicing (gerund), to study (infinitive), mental preparation (noun) — not parallel.',
      ],
      explanation: 'All items in a list must use the same grammatical form. Three gerunds (-ing forms) create perfect parallel structure.',
    },
  },
  {
    id: 50,
    domain: 'Standard English Conventions',
    subtopic: 'Punctuation',
    question: 'Which sentence correctly uses an apostrophe?',
    options: [
      { label: 'A', value: 'The childrens\' artwork was displayed in the hallway.' },
      { label: 'B', value: 'The children\'s artwork was displayed in the hallway.' },
      { label: 'C', value: 'The childrens artwork was displayed in the hallway.' },
      { label: 'D', value: 'The children\'s artwork\'s were displayed in the hallway.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Children" is an irregular plural — it does not end in -s.',
        'For irregular plurals not ending in -s, add \'s to show possession.',
        '"Children\'s" = correct possessive form.',
        '"Childrens\'" would be correct only if the plural were "childrens" — which it isn\'t.',
      ],
      explanation: 'Irregular plurals (children, men, women, mice) form the possessive by adding \'s, not just an apostrophe.',
    },
  },
  {
    id: 51,
    domain: 'Standard English Conventions',
    subtopic: 'Verb Tense',
    question: 'The company _______ its new product line next spring, pending regulatory approval.',
    options: [
      { label: 'A', value: 'launched' },
      { label: 'B', value: 'launches' },
      { label: 'C', value: 'will launch' },
      { label: 'D', value: 'had launched' },
    ],
    correctAnswer: 'C',
    solution: {
      steps: [
        '"Next spring" indicates a future time.',
        'Future events require future tense: "will launch."',
        '"Launched" is past tense — contradicts "next spring."',
        '"Had launched" is past perfect — also contradicts future context.',
      ],
      explanation: 'Time markers like "next spring," "tomorrow," or "in the future" signal that future tense is required.',
    },
  },
  {
    id: 52,
    domain: 'Standard English Conventions',
    subtopic: 'Pronoun Usage',
    question: 'The award was given to _______ worked the hardest — Maria and me, or Maria and I?',
    options: [
      { label: 'A', value: 'Maria and me' },
      { label: 'B', value: 'Maria and I' },
      { label: 'C', value: 'Maria and myself' },
      { label: 'D', value: 'Maria and us' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"The award was given to ___" — "to" is a preposition, so object pronoun seems right.',
        'But wait: "who worked the hardest" — this is a subject clause.',
        'The pronoun is the subject of "worked": "I worked the hardest."',
        'Subject pronoun "I" is correct here.',
        'Test: "The award was given to I worked the hardest" — "I" is the subject of the relative clause.',
      ],
      explanation: 'When a pronoun is the subject of a verb (worked), use the subject form (I), even after a preposition.',
    },
  },
  {
    id: 53,
    domain: 'Standard English Conventions',
    subtopic: 'Modifiers',
    question: 'Which sentence avoids a misplaced modifier?',
    options: [
      { label: 'A', value: 'I almost drove my kids to school every day last year.' },
      { label: 'B', value: 'I drove my kids to school almost every day last year.' },
      { label: 'C', value: 'Almost I drove my kids to school every day last year.' },
      { label: 'D', value: 'I drove almost my kids to school every day last year.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        '"Almost" should modify "every day" — meaning nearly every day.',
        'Choice B: "almost every day" — "almost" correctly modifies "every day."',
        'Choice A: "almost drove" — implies you nearly drove but didn\'t. Wrong meaning.',
        'Choice D: "almost my kids" — implies you drove something other than your kids. Wrong.',
      ],
      explanation: 'Place modifiers immediately before the word they modify. "Almost every day" means "nearly every day."',
    },
  },
  {
    id: 54,
    domain: 'Standard English Conventions',
    subtopic: 'Parallel Structure',
    question: 'Which sentence uses parallel structure correctly?',
    options: [
      { label: 'A', value: 'The new policy requires employees to clock in, completing their tasks, and to clock out.' },
      { label: 'B', value: 'The new policy requires employees to clock in, to complete their tasks, and to clock out.' },
      { label: 'C', value: 'The new policy requires employees to clock in, complete their tasks, and clocking out.' },
      { label: 'D', value: 'The new policy requires employees clocking in, to complete their tasks, and clock out.' },
    ],
    correctAnswer: 'B',
    solution: {
      steps: [
        'The list follows "requires employees": to clock in, to complete, to clock out.',
        'Choice B: to clock in, to complete, to clock out — all infinitives. Parallel.',
        'Choice A: to clock in, completing (gerund), to clock out — not parallel.',
        'Choice C: to clock in, complete (bare infinitive), clocking out (gerund) — not parallel.',
      ],
      explanation: 'When a list follows "to + verb," all items should use the same infinitive form for parallel structure.',
    },
  },
];

export const RW_DOMAIN_COLORS: Record<RWMockQuestion['domain'], string> = {
  'Information & Ideas': 'violet',
  'Craft & Structure': 'rose',
  'Expression of Ideas': 'amber',
  'Standard English Conventions': 'teal',
};

export const RW_DOMAIN_QUESTION_COUNTS: Record<RWMockQuestion['domain'], number> = {
  'Information & Ideas': 14,
  'Craft & Structure': 14,
  'Expression of Ideas': 13,
  'Standard English Conventions': 13,
};
